import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header/Header';
import SEO from '../components/SEO/SEO';
import BubblesVisualization from '../components/BubblesVisualization/BubblesVisualization';
import BigVsSmall from '../components/BigVsSmall/BigVsSmall';
import CategoryModal from '../components/CategoryModal/CategoryModal';
import { useBubblesData } from '../hooks/useBubblesData';
import './BubblesPage.css';

function BubblesPage() {
  const { t } = useTranslation();
  const [mode, setMode] = useState('sectors');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const { data, loading, error } = useBubblesData(mode, 60000);

  const faqData = [
    {
      question: t('bubbles.faq.q1', 'Why use bubble charts instead of tables?'),
      answer: t('bubbles.faq.a1', 'Tables show numbers sequentially — you scan row by row. Bubbles show the entire market at once. Your brain processes visual patterns faster than numbers. In one glance, you see which sectors are hot, which coins are pumping, and how market cap distributes across the space. Professional traders use bubble charts to identify opportunities that spreadsheets hide.')
    },
    {
      question: t('bubbles.faq.q2', 'How do I identify sector rotation?'),
      answer: t('bubbles.faq.a2', 'Switch to Sectors mode and compare performance across categories. When one sector shows consistent green while others lag, money is rotating into that narrative. For instance, if DeFi bubbles turn green after an AI rally, capital is likely flowing from AI to DeFi. Follow the green trail to anticipate the next move.')
    },
    {
      question: t('bubbles.faq.q3', 'What does Big vs Small tell me?'),
      answer: t('bubbles.faq.a3', 'It measures market risk appetite. When large caps (BTC, ETH) outperform small caps, institutions are de-risking — not the time for moonshot bets. When small caps outperform, retail is aggressive and higher-beta plays work better. Use this to calibrate position sizing and coin selection.')
    },
    {
      question: t('bubbles.faq.q4', 'Can I click on sectors for details?'),
      answer: t('bubbles.faq.a4', 'Yes. Click any sector bubble to open a modal showing all coins in that category with individual performance metrics. This lets you drill down from macro (sector trend) to micro (specific coin selection) within the same interface.')
    }
  ];

  // Add FAQ Schema to page
  useEffect(() => {
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqData.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    };

    let faqScript = document.querySelector('script[data-schema="bubbles-faq"]');
    if (!faqScript) {
      faqScript = document.createElement('script');
      faqScript.setAttribute('type', 'application/ld+json');
      faqScript.setAttribute('data-schema', 'bubbles-faq');
      document.head.appendChild(faqScript);
    }
    faqScript.textContent = JSON.stringify(faqSchema);

    return () => {
      const script = document.querySelector('script[data-schema="bubbles-faq"]');
      if (script) script.remove();
    };
  }, [faqData]);

  // Handle category click (open modal)
  const handleCategoryClick = useCallback((category) => {
    setSelectedCategory(category);
  }, []);

  // Close modal
  const handleCloseModal = useCallback(() => {
    setSelectedCategory(null);
  }, []);

  return (
    <div className="bubbles-page">
      <SEO page="bubbles" />
      <Header />

      <div className="bubbles-page-content">
        {/* Page Title */}
        <h1 className="bubbles-title">{t('bubbles.pageTitle', 'Crypto Bubbles & Smart Money')}</h1>
        <p className="bubbles-subtitle">{t('bubbles.pageSubtitle', 'Visualize crypto narratives and follow the Smart Money flow')}</p>

        {error && (
          <div className="bubbles-error">
            <span>{error}</span>
          </div>
        )}

        <div className="bubbles-grid">
          {/* Left: Bubbles Visualization (66%) */}
          <div className="bubbles-left">
            <div className="bubbles-panel">
              {/* Controls inside panel */}
              <div className="bubbles-panel-controls">
                <div className="bubbles-controls-inner">
                  {/* Mode Switcher */}
                  <div className="bubbles-mode-toggle">
                    <button
                      className={mode === 'sectors' ? 'active' : ''}
                      onClick={() => setMode('sectors')}
                    >
                      SECTORS
                    </button>
                    <button
                      className={mode === 'top100' ? 'active' : ''}
                      onClick={() => setMode('top100')}
                    >
                      TOP 100
                    </button>
                  </div>
                </div>
              </div>

              {/* Visualization */}
              <BubblesVisualization
                data={data}
                mode={mode}
                onCategoryClick={handleCategoryClick}
              />
            </div>
          </div>

          {/* Right: Big vs Small Panel (33%) */}
          <div className="bubbles-right">
            <BigVsSmall />
          </div>
        </div>

        {/* SEO Content Section */}
        <section className="bubbles-seo-section">
          <div className="bubbles-info-grid">
            <article className="bubbles-info-card">
              <h2>{t('bubbles.whatIsBubbles', 'What is Crypto Bubbles Visualization?')}</h2>
              <p>
                {t('bubbles.whatIsBubblesText', 'Unlike traditional price tables, our bubble chart lets you instantly spot market leaders and laggards. Each bubble\'s size reflects market cap — Bitcoin and Ethereum dominate visually, while smaller altcoins cluster proportionally. Color intensity shows momentum: deep green signals strong rallies, deep red indicates selloffs. This visual approach helps traders identify rotation between large caps and small caps within seconds.')}
              </p>
            </article>

            <article className="bubbles-info-card">
              <h2>{t('bubbles.sectorsExplained', 'Crypto Sectors & Narratives')}</h2>
              <p>
                {t('bubbles.sectorsExplainedText', 'Sector mode groups coins by their narrative — DeFi, AI, Gaming, Layer 1, Layer 2, Memecoins, and more. When one sector outperforms, capital often rotates to related projects. For example, if AI tokens rally, projects with AI integrations typically follow. Use sector performance to anticipate where money flows next and position accordingly.')}
              </p>
            </article>

            <article className="bubbles-info-card">
              <h2>{t('bubbles.smartMoneyTitle', 'Big vs Small Comparison')}</h2>
              <p>
                {t('bubbles.smartMoneyText', 'The right panel compares large-cap performance against small-cap altcoins. When large caps outperform, it often signals risk-off sentiment — investors prefer established assets. When small caps lead, it indicates risk appetite and potential altseason conditions. This metric helps time entries into higher-beta altcoins.')}
              </p>
            </article>
          </div>

          {/* FAQ Section */}
          <div className="bubbles-faq-section">
            <h2>{t('bubbles.faqTitle', 'Frequently Asked Questions')}</h2>
            <div className="bubbles-faq-list">
              {faqData.map((faq, index) => (
                <div
                  key={index}
                  className={`bubbles-faq-item ${expandedFaq === index ? 'expanded' : ''}`}
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                >
                  <div className="bubbles-faq-question">
                    <h3>{faq.question}</h3>
                    <span className="bubbles-faq-toggle">{expandedFaq === index ? '−' : '+'}</span>
                  </div>
                  {expandedFaq === index && (
                    <div className="bubbles-faq-answer">
                      <p>{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>

      {/* Category Modal */}
      {selectedCategory && (
        <CategoryModal
          category={selectedCategory}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
}

export default BubblesPage;
